import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MarkantElementsModule } from '@markant/elements-angular';
import { TranslocoModule } from '@jsverse/transloco';
import { FormsModule } from '@angular/forms';
import { NavigationItemComponent } from './navigation-item/navigation-item.component';
import { AuthenticatedUserService } from '@markant-platform/shared/user/data-access';
import { StaticRole } from '@markant-platform/shared/user/model';
import { filter, map, startWith, tap } from 'rxjs';
import { NavigationItem } from '@markant-platform/shared/navigation/model';
import { NavigationEnd, Router } from '@angular/router';
import { drawerAnimations, drawerFadeItemsAnimations, toggleAnimations } from './configuration/navigation.animation';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationAccordionComponent } from './navigation-accordion/navigation-accordion.component';
import { DrawerToggleComponent } from './drawer-toggle/drawer-toggle.component';
import { OrganizationService } from '@markant-platform/shared/organization/data-access';
import { OutsideClickDirective } from '@markant-platform/cartridge/util';
import { getAdminChildrenFromRoles, getAdminRouteFromRoles } from './configuration/navigation.util';
import { NAVIGATION_USER } from './configuration/navigation.config';

@Component({
  standalone: true,
  selector: 'markant-platform-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MarkantElementsModule,
    TranslocoModule,
    FormsModule,
    NavigationItemComponent,
    NavigationAccordionComponent,
    DrawerToggleComponent,
    OutsideClickDirective,
  ],
  animations: [drawerAnimations, toggleAnimations, drawerFadeItemsAnimations],
})
export class NavigationComponent implements OnInit {
  private readonly authenticatedUserService: AuthenticatedUserService = inject(AuthenticatedUserService);
  private readonly router: Router = inject(Router);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly organizationService: OrganizationService = inject(OrganizationService);

  readonly DEFAULT_LABEL = 'Organisation';
  readonly ORG_LABEL = '%organization%';

  $isOrganizationAdmin = signal(false);
  $menuState = signal<'in' | 'out'>('out');
  $dockerItems = signal<NavigationItem[]>([...NAVIGATION_USER]);
  $activeItem = signal<NavigationItem | undefined>({ ...NAVIGATION_USER[0] });

  $drawerToggleIcon = computed(() => (this.$menuState() === 'in' ? 'm_chevron-left' : 'm_chevron-right'));

  ngOnInit(): void {
    this.authenticatedUserService
      .getUserRoles()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(roles => roles.map(({ name }) => name as StaticRole)),
        tap(roles => this.$isOrganizationAdmin.set(roles.includes(StaticRole.ORGANIZATION_ADMIN))),
        map(roles => this.buildNavigationItems(roles))
      )
      .subscribe(items => {
        this.$dockerItems.set(
          items.map(item => ({
            ...item,
            isActive: this.isRouteWithinHierarchy(item, this.router.url),
          }))
        );
      });

    this.router.events
      .pipe(
        startWith(new NavigationEnd(0, this.router.url, this.router.url)),
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ urlAfterRedirects }) => {
        this.setDockItems(urlAfterRedirects);
      });
  }

  onNavItemClick(navigationItem: NavigationItem): void {
    const { children, route } = navigationItem;

    if (children.length) {
      if (this.isRouteWithinHierarchy(navigationItem, this.router.url)) {
        this.toggleDrawerOpen();
      } else if (route) {
        this.toggleDrawerOpen();
        this.router.navigateByUrl(route);
      }
    } else if (route) {
      this.closeDrawer();
      this.router.navigateByUrl(route);
    }
  }

  toggleDrawerOpen(): void {
    this.$menuState.update(previousValue => (previousValue === 'out' ? 'in' : 'out'));
  }

  closeDrawer(): void {
    this.$menuState.set('out');
  }

  private buildNavigationItems(roles: StaticRole[]): NavigationItem[] {
    const isPFA = roles.includes(StaticRole.PLATFORM_ADMIN);
    const isOA = roles.includes(StaticRole.ORGANIZATION_ADMIN);
    const isSA = roles.includes(StaticRole.LEISTUNG_ADMIN);
    const isOM = roles.includes(StaticRole.ORGANIZATION_MEMBER);

    if (isPFA || isOA || isSA || isOM) {
      return [
        ...NAVIGATION_USER,
        {
          label: 'administration',
          image: 'admin-area',
          route: getAdminRouteFromRoles(roles),
          isActive: false,
          isCollapsed: true,
          children: getAdminChildrenFromRoles(roles),
          isTitle: false,
        },
      ];
    }

    return NAVIGATION_USER;
  }

  private setDockItems(currentUrl: string): void {
    this.$dockerItems.update(items => {
      return items.map(dockerItem => ({
        ...dockerItem,
        isActive: this.isRouteWithinHierarchy(dockerItem, currentUrl),
      }));
    });

    const activeItem = this.$dockerItems().find(dockerItem => this.isRouteWithinHierarchy(dockerItem, currentUrl));

    if (activeItem) {
      if (activeItem.children.length) {
        this.$activeItem.update(() => {
          return {
            ...activeItem,
            isActive: true,
            children: [...this.updateDrawerActiveStateAndCollapse(activeItem.children, currentUrl)],
          };
        });
        this.$isOrganizationAdmin() && this.setOrganizationName();
      } else {
        this.$activeItem.set({ ...activeItem, isActive: true });
        this.closeDrawer();
      }
    } else {
      this.$activeItem.set(undefined);
    }
  }

  private updateDrawerActiveStateAndCollapse(
    navigationItems: NavigationItem[],
    currentRoute: string
  ): NavigationItem[] {
    if (navigationItems.length) {
      navigationItems = navigationItems.map(navigationItem => {
        navigationItem.isActive = this.isRouteWithinHierarchy(navigationItem, currentRoute);

        if (navigationItem.children.length) {
          navigationItem.children = navigationItem.children.map(childItem => {
            childItem.isActive = this.isRouteWithinHierarchy(childItem, currentRoute);
            return childItem;
          });

          const isAnyChildActive = navigationItem.children.some(navigationItemChild => navigationItemChild.isActive);

          navigationItem.isActive = navigationItem.isActive || isAnyChildActive;
          navigationItem.isCollapsed = !isAnyChildActive;
        }

        return navigationItem;
      });
    }

    return [...navigationItems];
  }

  private isRouteWithinHierarchy(navigationItem: NavigationItem, currentRoute: string): boolean {
    if (
      navigationItem.route === currentRoute ||
      (navigationItem.route === '/' && currentRoute.includes('widget-selection'))
    ) {
      return true;
    }

    if (navigationItem.children.length) {
      return navigationItem.children.some(navigationItemChild => {
        if (currentRoute !== '/' && navigationItemChild.route) {
          const isActive = currentRoute.includes(navigationItemChild.route);

          if (!isActive && navigationItemChild.children.length) {
            return this.isRouteWithinHierarchy(navigationItemChild, currentRoute);
          }

          return isActive;
        }

        return false;
      });
    }

    return false;
  }

  private setOrganizationName(): void {
    this.organizationService
      .getCurrentOrganization()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(organizationDetailData => {
        if (this.$activeItem()) {
          this.$activeItem.update(
            navigationItem =>
              ({
                ...navigationItem,
                children: navigationItem?.children.map(navigationItemChild => {
                  return {
                    ...navigationItemChild,
                    ...(navigationItemChild.label === this.ORG_LABEL && {
                      label: organizationDetailData?.name ?? this.DEFAULT_LABEL,
                    }),
                  };
                }),
              }) as NavigationItem
          );
        }
      });
  }
}
